<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { FLOW_NODE_META } from './constants';
import FlowBranchEditor from './FlowBranchEditor.vue';
import FlowKeyValueEditor from './FlowKeyValueEditor.vue';
import FlowWaitPromptEditor from './FlowWaitPromptEditor.vue';

const props = defineProps({
  nodeId: { type: String, required: true },
  node: { type: Object, required: true },
  targetIds: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  canMoveUp: { type: Boolean, default: false },
  canMoveDown: { type: Boolean, default: false },
  showDragHandle: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:node',
  'remove',
  'move-up',
  'move-down',
  'changeType',
]);

const { t } = useI18n();

const typeOptions = computed(() => [
  { value: 'message', label: t('FLOWS.NODE_TYPE_MESSAGE') },
  { value: 'wait_input', label: t('FLOWS.NODE_TYPE_WAIT_INPUT') },
  { value: 'branch', label: t('FLOWS.NODE_TYPE_BRANCH') },
  { value: 'add_label', label: t('FLOWS.NODE_TYPE_ADD_LABEL') },
  { value: 'assign_team', label: t('FLOWS.NODE_TYPE_ASSIGN_TEAM') },
  { value: 'whatsapp_template', label: t('FLOWS.NODE_TYPE_WHATSAPP_TEMPLATE') },
]);

const meta = computed(() => FLOW_NODE_META[props.node.type] || {});

const teamSelectOptions = computed(() =>
  props.teams.map(tm => ({
    id: String(tm.id),
    name: tm.name,
  }))
);

const labelSelectOptions = computed(() =>
  props.labels.map(lb => ({
    id: lb.title,
    name: lb.title,
  }))
);

const patch = partial => {
  emit('update:node', { ...props.node, ...partial });
};

const onTypeChange = event => {
  emit('changeType', { nodeId: props.nodeId, newType: event.target.value });
};

const onNextChange = event => {
  patch({ next: event.target.value });
};

const branchList = computed({
  get: () => props.node.branches || [],
  set: v => emit('update:node', { ...props.node, branches: v }),
});

const waitDefaultNext = computed({
  get: () => props.node.default_next || '',
  set: v => emit('update:node', { ...props.node, default_next: v }),
});

const onWaitPromptUpdate = payload => {
  patch({
    prompt_type: payload.prompt_type,
    prompt_options: payload.prompt_options,
  });
};
</script>

<template>
  <div class="relative flex w-full min-w-0 items-start gap-2">
    <Button
      v-if="showDragHandle"
      ghost
      sm
      slate
      icon="i-lucide-grip-vertical"
      type="button"
      class="flow-node-drag mt-2 flex-shrink-0 cursor-move"
    />
    <div
      class="min-w-0 flex-1 rounded-lg border-l-4 border-n-blue-9 p-3 shadow-sm outline outline-1 outline-n-weak bg-n-background dark:bg-n-solid-1"
    >
      <div class="mb-3 flex flex-wrap items-center gap-2">
        <Icon
          v-if="meta.icon"
          :icon="meta.icon"
          class="size-5 text-n-slate-11"
        />
        <span class="font-mono text-xs text-n-slate-10">{{ nodeId }}</span>
        <template v-if="node.type !== 'start' && node.type !== 'end'">
          <select
            :value="node.type"
            class="min-w-[10rem] h-8 rounded-md border-0 bg-n-alpha-2 px-2 text-sm font-medium text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="onTypeChange"
          >
            <option
              v-for="opt in typeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </template>
        <span
          v-else
          class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium capitalize text-n-slate-12"
        >
          {{ node.type }}
        </span>
        <div class="ml-auto flex flex-wrap items-center gap-1">
          <Button
            v-if="node.type !== 'start' && node.type !== 'end'"
            faded
            slate
            xs
            icon="i-lucide-arrow-up"
            :disabled="!canMoveUp"
            :aria-label="$t('FLOWS.MOVE_UP')"
            @click="emit('move-up')"
          />
          <Button
            v-if="node.type !== 'start' && node.type !== 'end'"
            faded
            slate
            xs
            icon="i-lucide-arrow-down"
            :disabled="!canMoveDown"
            :aria-label="$t('FLOWS.MOVE_DOWN')"
            @click="emit('move-down')"
          />
          <Button
            v-if="node.type !== 'start' && node.type !== 'end'"
            faded
            ruby
            sm
            icon="i-lucide-trash-2"
            :aria-label="$t('FLOWS.REMOVE_STEP')"
            @click="emit('remove')"
          />
        </div>
      </div>

      <!-- start -->
      <div v-if="node.type === 'start'" class="space-y-2">
        <label class="block text-label-small text-n-slate-11" for="start-next">
          {{ $t('FLOWS.STARTS_AT') }}
        </label>
        <select
          id="start-next"
          :value="node.next"
          class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          @change="onNextChange"
        >
          <option v-for="tid in targetIds" :key="tid" :value="tid">
            {{ tid }}
          </option>
        </select>
      </div>

      <!-- message -->
      <div v-else-if="node.type === 'message'" class="space-y-3">
        <label class="block text-heading-3 text-n-slate-12" for="msg-content">
          {{ $t('FLOWS.MESSAGE_BODY') }}
        </label>
        <textarea
          id="msg-content"
          :value="node.content"
          rows="4"
          class="block w-full rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          @input="patch({ content: $event.target.value })"
        />
        <p class="text-xs leading-relaxed text-n-slate-11">
          {{ $t('FLOWS.MESSAGE_VARIABLES_HELP') }}
        </p>
        <FlowKeyValueEditor
          :model-value="node.set_variables || {}"
          :title="$t('FLOWS.FLOW_SET_VARIABLES')"
          :description="$t('FLOWS.FLOW_SET_VARIABLES_HELP')"
          :key-label="$t('FLOWS.KEY_VALUE_NAME')"
          :value-label="$t('FLOWS.KEY_VALUE_CONTENT')"
          :remove-aria-label="$t('FLOWS.KEY_VALUE_REMOVE_ROW')"
          @update:model-value="patch({ set_variables: $event })"
        />
        <FlowKeyValueEditor
          :model-value="node.message_content_attributes || {}"
          :title="$t('FLOWS.FLOW_MESSAGE_ATTRS')"
          :description="$t('FLOWS.FLOW_MESSAGE_ATTRS_HELP')"
          :key-label="$t('FLOWS.KEY_VALUE_FIELD')"
          :value-label="$t('FLOWS.KEY_VALUE_CONTENT')"
          :remove-aria-label="$t('FLOWS.KEY_VALUE_REMOVE_ROW')"
          @update:model-value="patch({ message_content_attributes: $event })"
        />
        <div>
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="msg-next"
          >
            {{ $t('FLOWS.NEXT_STEP') }}
          </label>
          <select
            id="msg-next"
            :value="node.next"
            class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="onNextChange"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
      </div>

      <!-- wait_input -->
      <div v-else-if="node.type === 'wait_input'" class="space-y-4">
        <FlowWaitPromptEditor
          :prompt-type="node.prompt_type || 'text'"
          :prompt-options="node.prompt_options || []"
          @update="onWaitPromptUpdate"
        />
        <Input
          :model-value="node.capture_variable || ''"
          :label="$t('FLOWS.CAPTURE_VARIABLE')"
          :placeholder="$t('FLOWS.CAPTURE_VARIABLE_PLACEHOLDER')"
          size="sm"
          @update:model-value="patch({ capture_variable: $event })"
        />
        <FlowBranchEditor
          v-model:branches="branchList"
          v-model:default-next="waitDefaultNext"
          :target-ids="targetIds"
        />
      </div>

      <!-- branch -->
      <div v-else-if="node.type === 'branch'" class="space-y-2">
        <label class="block text-label-small text-n-slate-11" for="br-next">
          {{ $t('FLOWS.NEXT_STEP') }}
        </label>
        <select
          id="br-next"
          :value="node.next"
          class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          @change="onNextChange"
        >
          <option v-for="tid in targetIds" :key="tid" :value="tid">
            {{ tid }}
          </option>
        </select>
      </div>

      <!-- add_label -->
      <div v-else-if="node.type === 'add_label'" class="space-y-3">
        <div>
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="lbl-pick"
          >
            {{ $t('FLOWS.PICK_LABEL') }}
          </label>
          <select
            id="lbl-pick"
            :value="node.label"
            class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="patch({ label: $event.target.value })"
          >
            <option value="">
              {{ $t('FLOWS.CHOOSE') }}
            </option>
            <option
              v-for="lb in labelSelectOptions"
              :key="lb.id"
              :value="lb.id"
            >
              {{ lb.name }}
            </option>
          </select>
        </div>
        <Input
          :model-value="node.label"
          :label="$t('FLOWS.LABEL_CUSTOM')"
          size="sm"
          @update:model-value="patch({ label: $event })"
        />
        <div>
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="al-next"
          >
            {{ $t('FLOWS.NEXT_STEP') }}
          </label>
          <select
            id="al-next"
            :value="node.next"
            class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="onNextChange"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
      </div>

      <!-- assign_team -->
      <div v-else-if="node.type === 'assign_team'" class="space-y-3">
        <label class="block text-label-small text-n-slate-11" for="team-pick">
          {{ $t('FLOWS.PICK_TEAM') }}
        </label>
        <select
          id="team-pick"
          :value="node.team_id"
          class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          @change="patch({ team_id: $event.target.value })"
        >
          <option value="">
            {{ $t('FLOWS.CHOOSE') }}
          </option>
          <option v-for="tm in teamSelectOptions" :key="tm.id" :value="tm.id">
            {{ tm.name }}
          </option>
        </select>
        <div>
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="at-next"
          >
            {{ $t('FLOWS.NEXT_STEP') }}
          </label>
          <select
            id="at-next"
            :value="node.next"
            class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="onNextChange"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
      </div>

      <!-- whatsapp_template -->
      <div v-else-if="node.type === 'whatsapp_template'" class="space-y-3">
        <Input
          :model-value="node.template_name"
          :label="$t('FLOWS.TEMPLATE_NAME')"
          size="sm"
          @update:model-value="patch({ template_name: $event })"
        />
        <div>
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="wt-next"
          >
            {{ $t('FLOWS.NEXT_STEP') }}
          </label>
          <select
            id="wt-next"
            :value="node.next"
            class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="onNextChange"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
      </div>

      <!-- end -->
      <p v-else-if="node.type === 'end'" class="mb-0 text-sm text-n-slate-11">
        {{ $t('FLOWS.END_NODE_HELP') }}
      </p>
    </div>
  </div>
</template>
